import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import * as React from 'react';
import { TableauUserOrderField } from '../../../gen-src/queries/globalTypes';

/*
 * The DataGrid uses "any" type for the row data, so we use these interfaces to make our grids more type-safe
 */

export interface TypedBodyCellProps<TRowData> extends BodyCell.Props {
  row: TypedRow<TRowData>;
}

export interface TypedRow<TRowData> extends DataGrid.Row {
  data: TRowData;
}

/**
 * This matches the DataGrid's row handler (used by the rowStore), but with a type parameter for the row data
 */
export type TypedRowHandler<TRowData> = (rows: Readonly<TypedRow<TRowData>>[]) => void;

/**
 * Our Paginated/GraphQL row store passes back error information along with the row information. We strip out the errors
 * in DataGridWithSynchronousRowStore and then give a normal TypedRowStore to the underlying data grid.
 */
export type TypedRowHandlerWithErrors<TRowData, TError> = (rows: Readonly<TypedRow<TRowData>>[], errors: ReadonlyArray<TError>) => void;


/**
 * This matches the DataGrid's RowStore, but with a type parameter for the row data
 */
export interface TypedRowStore<TRowData> extends DataGrid.RowStore {
  getItems(start: number, end: number, rowHandler: TypedRowHandler<TRowData>): void;
}

/**
 * Our Paginated/GraphQL row stores implement this interface, in order to return error data along with the rows.
 * We strip out the errors in DataGridWithSynchronousRowStore and then give a normal TypedRowStore to the underlying data grid.
 */
export interface TypedRowStoreWithErrors<TRowData, TError> extends DataGrid.RowStore {
  getItems(start: number, end: number, rowHandler: TypedRowHandlerWithErrors<TRowData, TError>): void;
}

export interface TypedRowStoreWithErrorsProps<TRowData, TError> {
  readonly rowStore: TypedRowStoreWithErrors<TRowData, TError>;
}

export interface UsualDisplayGridProps<TRowData, TError> extends TypedRowStoreWithErrorsProps<TRowData, TError> {
  readonly rowCount?: number | null;
  readonly emptyGridMessage?: string;
}

// Enforce a column's `dataKey` to be a key that actually exists on the row data
// and force use of cell formatters on all columns.
export type TypedColumn<TRowData, OrderFields extends string = ''> =
  SortableColumn<TRowData, OrderFields> | ConditionallySortableColumn<TRowData> | UnsortableColumn<TRowData>;

// TODO TFS1107342: Replace ownerName with TableauUserOrderField.OWNERNAME
type ConditionallySortableOrderFields = TableauUserOrderField.NAME | 'ownerName';
type SortableOrderFields<OrderFields extends string> = Exclude<OrderFields, ConditionallySortableOrderFields>;

export type SortableColumn<TRowData, OrderFields extends string> =
  DataGrid.Column & CommonTypedColumn<TRowData> & {
    sortable: true;
    dataKey: SortableOrderFields<OrderFields>;
  };

export type ConditionallySortableColumn<TRowData> =
  DataGrid.Column & CommonTypedColumn<TRowData> & {
    sortable: boolean;
    dataKey: ConditionallySortableOrderFields;
  };

export type UnsortableColumn<TRowData> =
  DataGrid.Column & CommonTypedColumn<TRowData> & {
    sortable: false;
    dataKey: typeof UnsortableColumnKey | typeof SelectionColumnKey;
  };

type CommonTypedColumn<TRowData> = {
  cellFormatter: React.Factory<TypedBodyCellProps<TRowData>>;
};

// Special column key for the selection column. Must be defined in this file
// (rather than in SelectionColumn.tsx) to avoid circular imports
export const SelectionColumnKey = 'selection';

export const UnsortableColumnKey = '';
